import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RoleGuard from './RoleGuard';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    industries: [],
    project_stages: [],
    stage_labels: {},
    project_flags: [],
    flag_labels: {},
    task_templates: []
  });
  
  // Modal states for adding items
  const [showAddIndustryModal, setShowAddIndustryModal] = useState(false);
  const [showAddStageModal, setShowAddStageModal] = useState(false);
  const [showAddFlagModal, setShowAddFlagModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/system-settings`, settings);
      alert('Настройки успешно сохранены!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Ошибка при сохранении настроек: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const addIndustry = () => {
    if (newItemName && newItemName.trim()) {
      setSettings({
        ...settings,
        industries: [...settings.industries, newItemName.trim()]
      });
      setNewItemName('');
      setShowAddIndustryModal(false);
    }
  };

  const removeIndustry = (index) => {
    setSettings({
      ...settings,
      industries: settings.industries.filter((_, i) => i !== index)
    });
  };

  const updateStageLabel = (stage, label) => {
    setSettings({
      ...settings,
      stage_labels: {
        ...settings.stage_labels,
        [stage]: label
      }
    });
  };

  const addProjectStage = () => {
    if (newItemName && newItemName.trim() && newItemLabel && newItemLabel.trim()) {
      setSettings({
        ...settings,
        project_stages: [...settings.project_stages, newItemName.trim()],
        stage_labels: {
          ...settings.stage_labels,
          [newItemName.trim()]: newItemLabel.trim()
        }
      });
      setNewItemName('');
      setNewItemLabel('');
      setShowAddStageModal(false);
    }
  };

  const removeProjectStage = (index) => {
    const stageToRemove = settings.project_stages[index];
    const newStageLabels = { ...settings.stage_labels };
    delete newStageLabels[stageToRemove];

    setSettings({
      ...settings,
      project_stages: settings.project_stages.filter((_, i) => i !== index),
      stage_labels: newStageLabels
    });
  };

  const addProjectFlag = () => {
    if (newItemName && newItemName.trim() && newItemLabel && newItemLabel.trim()) {
      const flagKey = newItemName.trim().toUpperCase();
      setSettings({
        ...settings,
        project_flags: [...settings.project_flags, flagKey],
        flag_labels: {
          ...settings.flag_labels,
          [flagKey]: newItemLabel.trim()
        }
      });
      setNewItemName('');
      setNewItemLabel('');
      setShowAddFlagModal(false);
    }
  };

  const removeProjectFlag = (index) => {
    const flagToRemove = settings.project_flags[index];
    const newFlagLabels = { ...settings.flag_labels };
    delete newFlagLabels[flagToRemove];

    setSettings({
      ...settings,
      project_flags: settings.project_flags.filter((_, i) => i !== index),
      flag_labels: newFlagLabels
    });
  };

  const addTask = () => {
    if (!newItemName.trim()) return;
    setSettings({
      ...settings,
      task_templates: [...settings.task_templates, newItemName.trim()]
    });
    setShowAddTaskModal(false);
    setNewItemName('');
  };

  const removeTask = (index) => {
    const newTasks = [...settings.task_templates];
    newTasks.splice(index, 1);
    setSettings({
      ...settings,
      task_templates: newTasks
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <RoleGuard allowedRoles={['admin']}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20">
              <div className="flex items-center">
                <div className="flex items-center min-w-0">
                  <img 
                    src="/profland-logo-new.jpg" 
                    alt="Profland CRM" 
                    className="h-10 w-auto mr-4 flex-shrink-0"
                  />
                  <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Profland CRM</h1>
                </div>
                <div className="hidden md:ml-10 md:flex md:space-x-8">
                  <Link to="/dashboard" className="nav-link">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    </svg>
                    Дашборд
                  </Link>
                  <Link to="/admin/users" className="nav-link">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    Пользователи
                  </Link>
                  <Link to="/admin/settings" className="nav-link active">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Настройки
                  </Link>
                </div>
              </div>
              <div className="flex items-center">
                <UserMenu />
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Header */}
            <div className="mb-8 flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Настройки системы
                </h1>
                <p className="text-gray-600">
                  Управление отраслями, этапами проектов и флагами
                </p>
              </div>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Сохранение...' : 'Сохранить изменения'}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Industries Section */}
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Отрасли
                </h3>
                <div className="space-y-2 mb-4">
                  {settings.industries.map((industry, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-900">{industry}</span>
                      <button
                        onClick={() => removeIndustry(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Удалить
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowAddIndustryModal(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors"
                >
                  + Добавить отрасль
                </button>
              </div>

              {/* Project Stages Section */}
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Этапы проектов
                </h3>
                <div className="space-y-2 mb-4">
                  {settings.project_stages.map((stage, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 font-mono">{stage}</span>
                        <button
                          onClick={() => removeProjectStage(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Удалить
                        </button>
                      </div>
                      <input
                        type="text"
                        value={settings.stage_labels[stage] || ''}
                        onChange={(e) => updateStageLabel(stage, e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                        placeholder="Название этапа"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowAddStageModal(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors"
                >
                  + Добавить этап
                </button>
              </div>

              {/* Project Flags Section */}
              <div className="bg-white/80 backdrop-blur-lg rounded-xl p-6 border border-white/20 lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Флаги проектов
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {settings.project_flags.map((flag, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 font-mono">{flag}</span>
                        <button
                          onClick={() => removeProjectFlag(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Удалить
                        </button>
                      </div>
                      <div className="text-lg mb-2">{settings.flag_labels[flag]}</div>
                      <input
                        type="text"
                        value={settings.flag_labels[flag] || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          flag_labels: {
                            ...settings.flag_labels,
                            [flag]: e.target.value
                          }
                        })}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                        placeholder="Название флага с эмодзи"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowAddFlagModal(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors"
                >
                  + Добавить флаг
                </button>
              </div>
            </div>

            {/* Info Section */}
            {/* Task Templates Section */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Шаблоны задач</h3>
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  + Добавить задачу
                </button>
              </div>
              <div className="space-y-2">
                {settings.task_templates && settings.task_templates.map((task, index) => (
                  <div key={index} className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                    <span className="text-gray-900">{task}</span>
                    <button
                      onClick={() => removeTask(index)}
                      className="text-red-600 hover:text-red-800 font-medium text-sm"
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Информация</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Изменения применятся после сохранения</li>
                      <li>Удаление используемых значений может повлиять на существующие проекты</li>
                      <li>Ключи этапов должны быть на английском языке (snake_case)</li>
                      <li>Ключи флагов должны быть в верхнем регистре</li>
                      <li>Шаблоны задач помогают пользователям быстро выбирать частые действия</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Add Industry Modal */}
        {showAddIndustryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Добавить отрасль
              </h3>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Название отрасли"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                autoFocus
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddIndustryModal(false);
                    setNewItemName('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={addIndustry}
                  disabled={!newItemName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Добавить
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Stage Modal */}
        {showAddStageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Добавить этап проекта
              </h3>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Ключ этапа (на английском)"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-3"
                autoFocus
              />
              <input
                type="text"
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                placeholder="Название этапа (на русском)"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddStageModal(false);
                    setNewItemName('');
                    setNewItemLabel('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={addProjectStage}
                  disabled={!newItemName.trim() || !newItemLabel.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Добавить
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Flag Modal */}
        {showAddFlagModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Добавить флаг проекта
              </h3>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Ключ флага (UPPERCASE)"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-3"
                autoFocus
              />
              <input
                type="text"
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                placeholder="Название флага (с эмодзи)"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddFlagModal(false);
                    setNewItemName('');
                    setNewItemLabel('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={addProjectFlag}
                  disabled={!newItemName.trim() || !newItemLabel.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Добавить
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Task Modal */}
        {showAddTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Добавить шаблон задачи
              </h3>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Например: Получить проект"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newItemName.trim()) {
                    addTask();
                  }
                }}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddTaskModal(false);
                    setNewItemName('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={addTask}
                  disabled={!newItemName.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Добавить
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RoleGuard>
  );
};

export default AdminSettingsPage;